import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Calendar, MapPin, Users, Plus, Edit, Trash2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const PopUpEventManager = () => {
  const [events, setEvents] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [selectedMenuItems, setSelectedMenuItems] = useState([]);

  const fetchData = useCallback(async () => {
    try {
      const [eventsRes, menuRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/popup-events?active_only=false`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/menu-items`, { withCredentials: true })
      ]);
      setEvents(eventsRes.data);
      setMenuItems(menuRes.data);
    } catch (error) {
      toast.error("Failed to load events");
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);

    const eventData = {
      title: formData.get('title'),
      description: formData.get('description'),
      location: formData.get('location'),
      event_date: new Date(formData.get('event_date')).toISOString(),
      start_time: formData.get('start_time'),
      end_time: formData.get('end_time'),
      menu_items: selectedMenuItems,
      max_orders: formData.get('max_orders') ? parseInt(formData.get('max_orders')) : null,
      pre_order_cutoff: new Date(formData.get('pre_order_cutoff')).toISOString(),
      image: formData.get('image') || null
    };

    try {
      await axios.post(`${BACKEND_URL}/api/admin/popup-events`, eventData, { withCredentials: true });
      toast.success("Pop-up event created!");
      setShowDialog(false);
      setSelectedMenuItems([]);
      fetchData();
      e.target.reset();
    } catch (error) {
      toast.error("Failed to create event");
      console.error(error);
    }
  };

  const toggleMenuItem = (itemId) => {
    if (selectedMenuItems.includes(itemId)) {
      setSelectedMenuItems(selectedMenuItems.filter(id => id !== itemId));
    } else {
      setSelectedMenuItems([...selectedMenuItems, itemId]);
    }
  };

  const toggleEventStatus = async (eventId, currentStatus) => {
    try {
      await axios.put(
        `${BACKEND_URL}/api/admin/popup-events/${eventId}`,
        { active: !currentStatus },
        { withCredentials: true }
      );
      toast.success(currentStatus ? "Event deactivated" : "Event activated");
      fetchData();
    } catch (error) {
      toast.error("Failed to update event");
    }
  };

  if (loading) {
    return <div className="text-center py-8">Loading events...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">Pop-Up Events</h2>
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogTrigger asChild>
            <Button className="bg-emerald-600 hover:bg-emerald-700">
              <Plus className="h-4 w-4 mr-2" />
              Create Event
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Create Pop-Up Event</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="title">Event Title *</Label>
                <Input id="title" name="title" placeholder="Weekend Brunch Pop-Up" required className="mt-1" />
              </div>

              <div>
                <Label htmlFor="description">Description *</Label>
                <textarea
                  id="description"
                  name="description"
                  placeholder="Join us for a special weekend brunch..."
                  required
                  rows="3"
                  className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="event_date">Event Date *</Label>
                  <Input
                    id="event_date"
                    name="event_date"
                    type="date"
                    min={new Date().toISOString().split('T')[0]}
                    required
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="location">Location *</Label>
                  <Input id="location" name="location" placeholder="Downtown Food Court" required className="mt-1" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="start_time">Start Time *</Label>
                  <Input id="start_time" name="start_time" type="time" required className="mt-1" />
                </div>
                <div>
                  <Label htmlFor="end_time">End Time *</Label>
                  <Input id="end_time" name="end_time" type="time" required className="mt-1" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="pre_order_cutoff">Pre-Order Cutoff *</Label>
                  <Input
                    id="pre_order_cutoff"
                    name="pre_order_cutoff"
                    type="datetime-local"
                    required
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="max_orders">Max Orders (Optional)</Label>
                  <Input id="max_orders" name="max_orders" type="number" min="1" placeholder="50" className="mt-1" />
                </div>
              </div>

              <div>
                <Label htmlFor="image">Image URL (Optional)</Label>
                <Input id="image" name="image" type="url" placeholder="https://..." className="mt-1" />
              </div>

              <div>
                <Label>Select Menu Items *</Label>
                <div className="grid grid-cols-2 gap-2 mt-2 max-h-60 overflow-y-auto border border-gray-200 rounded-md p-3">
                  {menuItems.map((item) => (
                    <label key={item.id} className="flex items-center space-x-2 cursor-pointer p-2 hover:bg-gray-50 rounded">
                      <input
                        type="checkbox"
                        checked={selectedMenuItems.includes(item.id)}
                        onChange={() => toggleMenuItem(item.id)}
                        className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                      />
                      <span className="text-sm">{item.name} (${item.price})</span>
                    </label>
                  ))}
                </div>
                {selectedMenuItems.length === 0 && (
                  <p className="text-sm text-red-600 mt-1">Please select at least one menu item</p>
                )}
              </div>

              <div className="flex gap-3">
                <Button
                  type="submit"
                  disabled={selectedMenuItems.length === 0}
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                >
                  Create Event
                </Button>
                <Button type="button" variant="outline" onClick={() => setShowDialog(false)} className="flex-1">
                  Cancel
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {events.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <Calendar className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-600">No pop-up events yet. Create your first event!</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {events.map((event) => (
            <Card key={event.id} className={`border-2 ${event.active ? 'border-emerald-200' : 'border-gray-200'}`}>
              <CardHeader className={event.active ? 'bg-emerald-50' : 'bg-gray-50'}>
                <div className="flex justify-between items-start">
                  <div>
                    <CardTitle className="text-xl">{event.title}</CardTitle>
                    <p className="text-sm text-gray-600 mt-1">{event.location}</p>
                  </div>
                  <Badge className={event.active ? 'bg-emerald-600' : 'bg-gray-400'}>
                    {event.active ? 'Active' : 'Inactive'}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="p-6 space-y-4">
                <p className="text-gray-700">{event.description}</p>

                <div className="space-y-2 text-sm">
                  <div className="flex items-center text-gray-600">
                    <Calendar className="h-4 w-4 mr-2 text-emerald-600" />
                    {new Date(event.event_date).toLocaleDateString()} • {event.start_time} - {event.end_time}
                  </div>
                  <div className="flex items-center text-gray-600">
                    <MapPin className="h-4 w-4 mr-2 text-emerald-600" />
                    {event.location}
                  </div>
                  <div className="flex items-center text-gray-600">
                    <Users className="h-4 w-4 mr-2 text-emerald-600" />
                    {event.current_orders} orders
                    {event.max_orders && ` / ${event.max_orders} max`}
                  </div>
                </div>

                <div>
                  <p className="text-sm font-semibold mb-2">Menu Items:</p>
                  <div className="flex flex-wrap gap-2">
                    {event.menu_items.map((item, idx) => (
                      <Badge key={`${event.id}-${item.id || item.name || idx}-${idx}`} variant="outline" className="text-xs">
                        {item.name}
                      </Badge>
                    ))}
                  </div>
                </div>

                <div className="pt-4 border-t flex gap-2">
                  <Button
                    size="sm"
                    variant={event.active ? "outline" : "default"}
                    onClick={() => toggleEventStatus(event.id, event.active)}
                    className="flex-1"
                  >
                    {event.active ? 'Deactivate' : 'Activate'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};
